import { M_Button, M_SimpleTable } from "../../../M_Components"
import { useEffect, useState, useRef } from "react";
import { genericEntityService } from "../../../services/genericEntityService";
import { FieldTypeEnum } from "../../../enums/fieldTypeEnum";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Check } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useGenericEntityContext } from "../context/genericEntityContext";
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import EditIcon from '@mui/icons-material/Edit';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import dayjs from "dayjs";
import FunctionsIcon from '@mui/icons-material/Functions';
import { Paginator } from 'primereact/paginator';
import './itemsGrid.css';
import { Chip } from "@mui/material";
import { genericEntityHelper, transformJsonToGridItems } from "./genericEntityHelper";
import { useErrorHandler } from "../shared/errorHandler";



export const ItemGrid = ({ entity, itemEditCallback, filters, hiddenFields, onSelectionChange, onOpenFunctions, onItemsLoad }) => {
    const { handleHttpError } = useErrorHandler();
    const [t, i18n] = useTranslation();
    const currentLanguage = i18n.resolvedLanguage;
    const { hasPermission } = useGenericEntityContext();

    // Helper function to get saved state from sessionStorage
    const getSavedState = () => {
        if (!entity?.EntityId) return null;
        const stateKey = `genericEntity_${entity.EntityId}_state`;
        const savedState = sessionStorage.getItem(stateKey);
        if (savedState) {
            try {
                return JSON.parse(savedState);
            } catch (e) {
                console.error('Error restoring entity state:', e);
                return null;
            }
        }
        return null;
    };

    const savedState = getSavedState();

    const [items, setItems] = useState(null);
    const [sort, setSort] = useState(savedState?.sort ?? null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);

    // Pagination state
    const [first, setFirst] = useState(savedState?.first ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(savedState?.rowsPerPage ?? 10);

    // Refs for overflow detection
    const containerRef = useRef(null);
    const tableRef = useRef(null);

    // Save state to sessionStorage whenever it changes
    useEffect(() => {
        if (!entity) return;

        const stateKey = `genericEntity_${entity.EntityId}_state`;
        const state = {
            first,
            rowsPerPage,
            sort
        };
        sessionStorage.setItem(stateKey, JSON.stringify(state));
    }, [entity?.EntityId, first, rowsPerPage, sort]);

    // Check for overflow and update hasOverflow state
    useEffect(() => {
        const checkOverflow = () => {
            if (containerRef.current && tableRef.current) {
                // Get the available viewport height
                const viewportHeight = window.innerHeight;

                // Get the table's position relative to viewport
                const tableRect = tableRef.current.getBoundingClientRect();
                const tableTop = tableRect.top;

                // Calculate space available from table top to bottom of viewport
                // Reserve space for paginator (70px)
                const availableHeight = viewportHeight - tableTop - 100;

                // Check if table content exceeds available space
                const tableContentHeight = tableRef.current.scrollHeight;
                const needsFixedPaginator = tableContentHeight > availableHeight;

                setHasOverflow(needsFixedPaginator);
            }
        };

        // Multiple checks to ensure we catch the overflow
        checkOverflow();
        const timeoutId1 = setTimeout(checkOverflow, 100);
        const timeoutId2 = setTimeout(checkOverflow, 300);
        const timeoutId3 = setTimeout(checkOverflow, 500);

        // Listen for window resize
        window.addEventListener('resize', checkOverflow);

        return () => {
            clearTimeout(timeoutId1);
            clearTimeout(timeoutId2);
            clearTimeout(timeoutId3);
            window.removeEventListener('resize', checkOverflow);
        };
    }, [items, loading, rowsPerPage, totalRecords]);

    const fieldTypeDic = entity ? entity.Fields?.reduce(
        (prev, cur) =>
            ({ ...prev, [cur.Code]: cur }), {}
    ) : {};

    // Build OData filter string from filters object
    const buildFilterString = () => {
        if (!filters || !entity?.Fields) return null;

        const filterClauses = Object.entries(filters)
            .filter(([key, value]) => {
                // Only apply filters that have meaningful values
                if (value === null || value === undefined || value === '') return false;

                const field = fieldTypeDic[key];
                if (!field) return false;

                // Skip boolean and attachment fields - will be filtered client-side
                if (field.Type === FieldTypeEnum.Boolean || field.Type === FieldTypeEnum.Attachment) {
                    return false;
                }

                // For text fields, check if it's a non-empty string
                if (field.Type === FieldTypeEnum.Text ||
                    field.Type === FieldTypeEnum.TextArea ||
                    field.Type === FieldTypeEnum.Email) {
                    return String(value).trim().length > 0;
                }

                // For other types (number, dropdown, date), any non-empty value is valid
                return true;
            })
            .map(([key, value]) => {
                const field = fieldTypeDic[key];
                if (!field) return null;

                // Handle different field types with appropriate OData syntax
                switch (field.Type) {
                    case FieldTypeEnum.Integer:
                    case FieldTypeEnum.Decimal:
                    case FieldTypeEnum.Currency:
                        // Numeric: fieldName eq 123
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                            return `${key} eq ${numValue}`;
                        }
                        return null;

                    case FieldTypeEnum.Dropdown:
                        // Dropdown (usually numeric ID): fieldName eq 1
                        const dropdownValue = parseInt(value);
                        if (!isNaN(dropdownValue)) {
                            return `${key} eq ${dropdownValue}`;
                        }
                        return null;

                    case FieldTypeEnum.Date:
                    case FieldTypeEnum.DateTime:
                        // Date: fieldName eq 2024-01-01T00:00:00Z
                        return `${key} eq ${value}`;

                    default:
                        // Text fields: contains(tolower(fieldName), 'text')
                        const textValue = String(value).toLowerCase().replace(/'/g, "''"); // Escape single quotes
                        return `contains(tolower(cast(${key}, 'Edm.String')), '${textValue}')`;
                }
            })
            .filter(clause => clause !== null)
            .join(' and ');

        return filterClauses.length > 0 ? filterClauses : null;
    };

    // Build OData orderby string from sort object
    const buildOrderByString = () => {
        if (!sort) return null;
        return `${sort.code} ${sort.order}`;
    };

    useEffect(() => {
        if (entity === null) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const filterString = buildFilterString();
                const hasClientSideFilters = filters && Object.keys(filters).length > 0;
                const hasActiveFilters = (filterString && filterString.length > 0) || hasClientSideFilters;
                const hasActiveSort = sort !== null;

                // If filters or sort are active, fetch ALL data for client-side processing
                const shouldFetchAll = hasActiveFilters || hasActiveSort;

                try {
                    let normalizedItems = [];
                    let totalCount = 0;

                    if (shouldFetchAll) {
                        // Fetch ALL data when filters or sort are active
                        const response = await genericEntityService.getItemsByEntity(entity.EntityId);

                        const responseData = response.data?.Value || response.data?.value;
                        const itemsArray = responseData?.Items || responseData?.items || [];

                        // Normalize property names
                        normalizedItems = itemsArray.map(item => ({
                            itemId: item.ItemId || item.itemId,
                            fields: item.Fields || item.fields,
                            attachments: item.Attachments || item.attachments || {}
                        }));

                        // Apply client-side filtering
                        if (hasActiveFilters && filters) {
                            
                            // Helper to parse DD-MM-YYYY HH:MM:SS format from backend
                            const parseBackendDate = (dateStr) => {
                                if (!dateStr) return null;
                                // Format: "08-01-2026 00:00:00" -> DD-MM-YYYY HH:MM:SS
                                const parts = dateStr.split(' ');
                                const dateParts = parts[0].split('-'); // [DD, MM, YYYY]
                                const timeParts = parts[1] ? parts[1].split(':') : [0, 0, 0]; // [HH, MM, SS]
                                
                                // Create Date: new Date(year, month-1, day, hour, min, sec)
                                return new Date(
                                    parseInt(dateParts[2]), // year
                                    parseInt(dateParts[1]) - 1, // month (0-based)
                                    parseInt(dateParts[0]), // day
                                    parseInt(timeParts[0]), // hour
                                    parseInt(timeParts[1]), // minute
                                    parseInt(timeParts[2]) // second
                                );
                            };
                            
                            normalizedItems = normalizedItems.filter(item => {
                                for (const [key, value] of Object.entries(filters)) {
                                    if (value !== null && value !== undefined && value !== '') {
                                        const field = fieldTypeDic[key];
                                        const itemValue = item.fields[key];
                                        
                                        // Boolean filtering
                                        if (field?.Type === FieldTypeEnum.Boolean || field?.Type === 'Boolean') {
                                            if (typeof value === 'boolean') {
                                                const itemBoolValue = itemValue === 'True' || itemValue === true;
                                                if (itemBoolValue !== value) {
                                                    return false;
                                                }
                                            }
                                            continue;
                                        }
                                        
                                        // Attachment filtering
                                        if (field?.Type === FieldTypeEnum.Attachment || field?.Type === 'Attachment') {
                                            const hasAttachment = item.attachments && item.attachments[key] && item.attachments[key].AttachmentId;
                                            if (typeof value === 'boolean') {
                                                if (value === true && !hasAttachment) return false;
                                                if (value === false && hasAttachment) return false;
                                            }
                                            continue;
                                        }
                                        
                                        // Date filtering - match exact date
                                        if (field?.Type === 'Date') {
                                            if (!itemValue) {
                                                return false;
                                            }
                                            
                                            const itemDate = parseBackendDate(itemValue);
                                            const filterDate = new Date(value);
                                            
                                            if (!itemDate || isNaN(itemDate.getTime())) {
                                                return false;
                                            }
                                            
                                            // Compare only date parts (ignore time)
                                            const itemDateStr = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`;
                                            const filterDateStr = `${filterDate.getFullYear()}-${String(filterDate.getMonth() + 1).padStart(2, '0')}-${String(filterDate.getDate()).padStart(2, '0')}`;
                                            
                                            if (itemDateStr !== filterDateStr) {
                                                return false;
                                            }
                                        }
                                        // Time filtering - match time part
                                        else if (field?.Type === 'Time') {
                                            if (!itemValue) {
                                                return false;
                                            }
                                            
                                            // Parse backend time (might include date: "07-01-2026 04:20:00")
                                            const itemDate = parseBackendDate(itemValue);
                                            
                                            // Parse filter value - could be Date object or time string
                                            let filterDate;
                                            if (value instanceof Date) {
                                                filterDate = value;
                                            } else if (typeof value === 'string') {
                                                // If it's a time string like "04:20:23"
                                                if (value.includes(':') && !value.includes(' ')) {
                                                    const timeParts = value.split(':');
                                                    filterDate = new Date();
                                                    filterDate.setHours(parseInt(timeParts[0]));
                                                    filterDate.setMinutes(parseInt(timeParts[1]));
                                                } else {
                                                    filterDate = new Date(value);
                                                }
                                            } else {
                                                filterDate = new Date(value);
                                            }
                                            
                                            if (!itemDate || isNaN(itemDate.getTime())) {
                                                return false;
                                            }
                                            
                                            if (!filterDate || isNaN(filterDate.getTime())) {
                                                return false;
                                            }
                                            
                                            // Compare only time parts (HH:MM)
                                            const itemTimeStr = `${String(itemDate.getHours()).padStart(2, '0')}:${String(itemDate.getMinutes()).padStart(2, '0')}`;
                                            const filterTimeStr = `${String(filterDate.getHours()).padStart(2, '0')}:${String(filterDate.getMinutes()).padStart(2, '0')}`;
                                            
                                            if (itemTimeStr !== filterTimeStr) {
                                                return false;
                                            }
                                        }
                                        // Datetime filtering - match date and time
                                        else if (field?.Type === 'Datetime') {
                                            if (!itemValue) {
                                                return false;
                                            }
                                            
                                            const itemDateTime = parseBackendDate(itemValue);
                                            const filterDateTime = new Date(value);
                                            
                                            if (!itemDateTime || isNaN(itemDateTime.getTime())) {
                                                return false;
                                            }
                                            
                                            // Compare up to minutes
                                            if (itemDateTime.getFullYear() !== filterDateTime.getFullYear() ||
                                                itemDateTime.getMonth() !== filterDateTime.getMonth() ||
                                                itemDateTime.getDate() !== filterDateTime.getDate() ||
                                                itemDateTime.getHours() !== filterDateTime.getHours() ||
                                                itemDateTime.getMinutes() !== filterDateTime.getMinutes()) {
                                                return false;
                                            }
                                        }
                                        // Text filtering - includes
                                        else {
                                            const fieldValue = String(itemValue || '').toLowerCase();
                                            const filterValue = String(value).toLowerCase();
                                            if (!fieldValue.includes(filterValue)) {
                                                return false;
                                            }
                                        }
                                    }
                                }
                                return true;
                            });
                        }

                        // Apply client-side sorting
                        if (hasActiveSort) {
                            normalizedItems.sort((a, b) => {
                                let aVal = a.fields[sort.code];
                                let bVal = b.fields[sort.code];
                                const field = fieldTypeDic[sort.code];

                                if (field?.Type === FieldTypeEnum.Decimal ||
                                    field?.Type === FieldTypeEnum.Integer ||
                                    field?.Type === FieldTypeEnum.Currency) {
                                    aVal = aVal === null || aVal === undefined || aVal === '' ? 0 : parseFloat(aVal);
                                    bVal = bVal === null || bVal === undefined || bVal === '' ? 0 : parseFloat(bVal);
                                    if (isNaN(aVal)) aVal = 0;
                                    if (isNaN(bVal)) bVal = 0;
                                    return sort.order === 'asc' ? aVal - bVal : bVal - aVal;
                                }

                                if (field?.Type === FieldTypeEnum.Date || field?.Type === FieldTypeEnum.DateTime) {
                                    const aDate = aVal ? new Date(aVal).getTime() : 0;
                                    const bDate = bVal ? new Date(bVal).getTime() : 0;
                                    return sort.order === 'asc' ? aDate - bDate : bDate - aDate;
                                }

                                aVal = aVal === null || aVal === undefined ? '' : String(aVal).toLowerCase();
                                bVal = bVal === null || bVal === undefined ? '' : String(bVal).toLowerCase();
                                if (aVal < bVal) return sort.order === 'asc' ? -1 : 1;
                                if (aVal > bVal) return sort.order === 'asc' ? 1 : -1;
                                return 0;
                            });
                        }

                        // Update total after filtering/sorting
                        totalCount = normalizedItems.length;

                        // Apply client-side pagination
                        normalizedItems = normalizedItems.slice(first, first + rowsPerPage);

                    } else {
                        // Use server-side pagination when no filters/sort (more efficient)
                        const response = await genericEntityService.getItemsByEntityPaginated(
                            entity.EntityId,
                            rowsPerPage,
                            first,
                            null,
                            null,
                            true
                        );

                        totalCount = response.data?.Count || response.data?.count || 0;
                        const responseData = response.data?.Value || response.data?.value;
                        let itemsArray = responseData?.Items || responseData?.items || responseData || [];

                        normalizedItems = itemsArray.map(item => ({
                            itemId: item.ItemId || item.itemId,
                            fields: item.Fields || item.fields,
                            attachments: item.Attachments || item.attachments || {}
                        }));
                    }

                    setItems(normalizedItems);
                    setTotalRecords(totalCount);
                    
                    // Notify parent with loaded items
                    if (onItemsLoad) {
                        onItemsLoad(normalizedItems);
                    }

                } catch (odataError) {
                    // Fallback: Use old API without OData
                    const response = await genericEntityService.getItemsByEntity(entity.EntityId);

                    const responseData = response.data?.Value || response.data?.value;
                    const itemsArray = responseData?.Items || responseData?.items || [];

                    // Normalize property names
                    let normalizedItems = itemsArray.map(item => ({
                        itemId: item.ItemId || item.itemId,
                        fields: item.Fields || item.fields,
                        attachments: item.Attachments || item.attachments || {}
                    }));

                    // Apply client-side filtering if filters exist
                    if (filterString && filters) {
                        normalizedItems = normalizedItems.filter(item => {
                            for (const [key, value] of Object.entries(filters)) {
                                if (value && value !== '') {
                                    const fieldValue = String(item.fields[key] || '').toLowerCase();
                                    const filterValue = String(value).toLowerCase();
                                    if (!fieldValue.includes(filterValue)) {
                                        return false;
                                    }
                                }
                            }
                            return true;
                        });
                    }

                    // Apply client-side sorting if sort exists
                    if (sort) {
                        normalizedItems.sort((a, b) => {
                            let aVal = a.fields[sort.code];
                            let bVal = b.fields[sort.code];

                            const field = fieldTypeDic[sort.code];
                            if (field?.Type === FieldTypeEnum.Decimal ||
                                field?.Type === FieldTypeEnum.Integer ||
                                field?.Type === FieldTypeEnum.Currency) {
                                aVal = Number(aVal) || 0;
                                bVal = Number(bVal) || 0;
                            }

                            if (aVal < bVal) return sort.order === 'asc' ? -1 : 1;
                            if (aVal > bVal) return sort.order === 'asc' ? 1 : -1;
                            return 0;
                        });
                    }

                    // Apply client-side pagination
                    const totalCount = normalizedItems.length;
                    const paginatedItems = normalizedItems.slice(first, first + rowsPerPage);

                    setItems(paginatedItems);
                    setTotalRecords(totalCount);
                    
                    // Notify parent with loaded items
                    if (onItemsLoad) {
                        onItemsLoad(paginatedItems);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                setItems([]);
                setTotalRecords(0);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [entity, first, rowsPerPage, filters, sort]);

    const handleFileDownload = (filename, attachmentId) => {
        genericEntityService.getFile(attachmentId)
            .then(function (response) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(link);
            })
            .catch(function (error) { handleHttpError(error); });
    }

    const handleItemDelete = (itemId) => {
        setLoading(true);
        genericEntityService.deleteItemById(entity.EntityId, itemId)
            .then(function (response) {
                // Trigger refetch by updating first if last item on page
                if (items.length === 1 && first > 0) {
                    setFirst(Math.max(0, first - rowsPerPage));
                } else {
                    setItems(items.filter(x => x.itemId !== itemId));
                }
            })
            .catch(function (error) { handleHttpError(error); })
            .finally(() => setLoading(false));
    }
    const handleSort = (code) => {
        const order = sort ? sort.order === 'asc' ? 'desc' : 'none' : 'asc';
        if (order === 'none') {
            setSort(null);
        } else {
            setSort({
                code: code,
                order: order
            })
        }
    }

    const baseColumns = [
        { field: 'id', header: 'Id', id: 'id' },
    ];
    const columns = entity?.Fields ? [
        ...baseColumns,
        ...entity.Fields?.filter(f => !hiddenFields.includes(f.Code)).map(f => {
            return {
                field: f.Code,
                id: f.Code,
                header: <div style={{ alignItems: "center", alignContent: 'center', display: "flex" }}>
                    {f.Captions[currentLanguage] ?? f.Code}
                    <M_Button
                        iconSmall
                        icon={sort && sort.code === f.Code ? sort.order === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon /> : <SwapVertIcon />}
                        onClick={() => handleSort(f.Code)}
                    />
                    {
                        (f.Type === FieldTypeEnum.Decimal || f.Type === FieldTypeEnum.Integer || f.Type === FieldTypeEnum.Currency) &&
                        <span 
                            style={{ marginLeft: '8px', color: '#666', cursor: 'pointer' }} 
                            title="Functions"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenFunctions) {
                                    onOpenFunctions(f.Code);
                                }
                            }}
                        >
                            <FunctionsIcon fontSize="small" />
                        </span>
                    }

                </div>
            }
        })
    ] : [
        ...baseColumns,
    ];

    const tableRows =
        items 
            ? genericEntityHelper.transformJsonToGridItems(items, fieldTypeDic, currentLanguage, handleFileDownload)
            : [];

    // Selection change handler
    const handleSelectionChange = (e) => {
        const selected = e.value;
        setSelectedItems(selected);

        // Extract only serializable data (no React components/DOM elements)
        const serializableSelection = selected.map(item => ({
            itemId: item.itemId,
            id: item.id,
            fields: item.fields
        }));

        // Persist to sessionStorage
        sessionStorage.setItem('selectedGenericItems', JSON.stringify(serializableSelection));

        // Notify parent component with clean data
        if (onSelectionChange) {
            onSelectionChange(serializableSelection);
        }
    };

    // Pagination change handler
    const onPageChange = (event) => {
        setFirst(event.first);
        setRowsPerPage(event.rows);
    };

    return (
        <div className="items-grid-container" ref={containerRef}>
            <div className={`items-grid-table ${hasOverflow ? 'with-fixed-paginator' : ''}`} ref={tableRef}>
                <DataTable
                    value={tableRows}
                    selection={selectedItems}
                    onSelectionChange={handleSelectionChange}
                    selectionMode="checkbox"
                    dataKey="id"
                    loading={loading}
                    onRowDoubleClick={(e) => itemEditCallback(e.data.itemId)}
                    emptyMessage={t('NgeNoRecords') || 'No records found'}
                    responsiveLayout="scroll"
                >
                    <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
                    {columns.map(col => (
                        <Column
                            key={col.id}
                            field={col.field}
                            header={col.header}
                            body={col.field === 'id' ? null : undefined}
                        />
                    ))}
                </DataTable>
            </div>
            <div className={`items-grid-paginator ${hasOverflow ? 'fixed' : ''}`}>
                <Paginator
                    first={first}
                    rows={rowsPerPage}
                    totalRecords={totalRecords}
                    rowsPerPageOptions={[5, 10, 20, 100]}
                    onPageChange={onPageChange}
                    template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                    currentPageReportTemplate={`${t('Showing')} {first} ${t('to')} {last} ${t('of')} {totalRecords} ${t('entries')}`}
                />
            </div>
        </div>
    )
}